/* eslint-disable no-await-in-loop */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

/* eslint-disable no-useless-return */
/* eslint-disable sonarjs/no-redundant-jump */
import type { Agent } from '@studio/models/agent';
import type { AutoTrackingState } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { pingSubjectForWorker } from './utils.js';
import type { ByteId } from '#~/api/index.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { Proxy } from '#~/agent/types.js';

/*
import type { Handlers } from '#~/agent/types.js';
import { hide, post } from '#~/notifications/index.js';

const notifyConnected = (deviceId: ByteId): void => {
  hide(`autotracking.provisioning.${deviceId.toString()}`);

  post({
    id: `autotracking.connected.${deviceId.toString()}`,
    message: 'Tracking service is connected. Searching for subjects...',
    title: 'Auto-Tracking Connected'
  });
};
*/

/** A class to manage auto tracking reservations for an agent. */
export class AutoTrackingStateManager {
  readonly #proxy: Proxy;

  /**
   * Creates a new auto tracking manager.
   *
   * @param agentId - The agent id.
   * @param agentStateSubordinate - The subordinate for the agent state.
   * @param conn - The nats connection to use.
   */
  public constructor(proxy: Proxy) {
    this.#proxy = proxy;
  }

  /**
   * Returns the agent id in hex.
   *
   * @returns The agent id in hex.
   */
  public get agentId(): Agent['id'] {
    return this.#proxy.id!;
  }

  /**
   * Returns the worker id for a camera.
   *
   * @param cameraId - The camera id.
   * @returns The worker id.
   */
  public getAutoTrackingWorkerIdForCameraId(cameraId: ByteId): Uint8Array | undefined {
    const reservations = this.#proxy.state.control?.studio?.autoTrackingReservation;

    if (!reservations) {
      return undefined;
    }

    const reservation = reservations[toHexFromId(cameraId)!];

    if (!reservation?.workerId) {
      return undefined;
    }

    return reservation.workerId;
  }

  /**
   * Gets the auto tracking state for a camera.
   *
   * @param cameraId - The camera id.
   * @returns The auto tracking state.
   */
  public getAutoTrackingStateForCameraId(cameraId: ByteId): AutoTrackingState | undefined {
    const workerID = this.getAutoTrackingWorkerIdForCameraId(cameraId);

    if (!workerID) {
      return undefined;
    }

    const states = this.#proxy.state.control?.studio?.autoTrackingWorker;

    return states?.[toHexFromId(workerID)!]?.state;
  }

  /**
   * Gets the auto tracking state for a camera.
   *
   * @returns The auto tracking state.
   */
  public getAssignableReservations(): AutoTrackingReservation[] {
    const reservations = this.#proxy.state.control?.studio?.autoTrackingReservation;

    if (!reservations) {
      return [];
    }

    return Object.entries(reservations)
      .filter(([_, reservation]) => !reservation.workerId)
      .map(([_, reservation]) => reservation)
      .filter((reservation): reservation is AutoTrackingReservation => reservation !== undefined);
  }

  /**
   * Gets the auto tracking state for a camera.
   *
   * @returns The auto tracking state.
   */
  public getAssignableWorkers(): Uint8Array[] {
    const workers = this.#proxy.state.control?.studio?.autoTrackingWorker;

    if (!workers) {
      return [];
    }

    const recentlyPinggedWorkers = Object.entries(workers).filter(
      ([_, worker]) =>
        worker?.state?.status?.lastPing !== undefined &&
        Date.now() - worker?.state?.status?.lastPing < 30_000
    );

    return recentlyPinggedWorkers
      .filter(([_, worker]) => {
        const reservationID = worker?.state?.status?.reservationId;

        if ((reservationID?.length ?? 0) === 0) {
          return true;
        }

        const reservation = Object.values(
          this.#proxy.state.control?.studio?.autoTrackingReservation ?? {}
        ).find((r) => toHexFromId(r?.cameraId) === reservationID);

        if (!reservation) {
          return true;
        }

        return true;
      })
      .map(([id]) => toIdFromHex(id))
      .filter((id): id is Uint8Array => id !== undefined)
      .sort((a, b) => {
        const aTime = workers[toHexFromId(a)!]?.state?.status?.lastPing ?? 0;
        const bTime = workers[toHexFromId(b)!]?.state?.status?.lastPing ?? 0;

        return bTime - aTime;
      });
  }

  /**
   * Ping the auto tracking worker to ensure it is still connected.
   *
   * @param workerId - The worker id.
   * @returns True if the worker is still connected.
   */
  public async pingWorker(workerId: Uint8Array): Promise<boolean> {
    try {
      const pingSubject = pingSubjectForWorker(toIdFromHex(this.#proxy.id)!, workerId);

      const requestData = new TextEncoder().encode('ping');

      const resp = await this.#proxy.connection.request(pingSubject, requestData, {
        timeout: 2000
      });

      return resp !== undefined;
    } catch {
      return false;
    }
  }
}
